/**
 * FleetTracko Fleet Management - Main App
 */

import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';

import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useAuthStore } from './stores/authStore';

// Lazy load all pages for better performance
const LoginPage = lazy(() =>
  import('./features/auth/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('./features/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./features/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const DashboardPage = lazy(() =>
  import('./features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const VehicleListPage = lazy(() =>
  import('./features/vehicles/VehicleListPage').then((m) => ({ default: m.VehicleListPage })),
);
const VehicleDetailPage = lazy(() =>
  import('./features/vehicles/VehicleDetailPage').then((m) => ({ default: m.VehicleDetailPage })),
);
const ProcessListPage = lazy(() =>
  import('./features/processes/ProcessListPage').then((m) => ({ default: m.ProcessListPage })),
);
const ProcessDetailPage = lazy(() =>
  import('./features/processes/ProcessDetailPage').then((m) => ({ default: m.ProcessDetailPage })),
);
const WorkshopDashboardPage = lazy(() =>
  import('./features/workshop/WorkshopDashboardPage').then((m) => ({
    default: m.WorkshopDashboardPage,
  })),
);
const ServicePlanningPage = lazy(() =>
  import('./features/service-planning/ServicePlanningPage').then((m) => ({
    default: m.ServicePlanningPage,
  })),
);
const ReportsPage = lazy(() =>
  import('./features/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const SettingsPage = lazy(() =>
  import('./features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const UserManagementPage = lazy(() =>
  import('./features/users/UserManagementPage').then((m) => ({ default: m.UserManagementPage })),
);
const FleetSignPage = lazy(() =>
  import('./features/fleet-sign/FleetSignPage').then((m) => ({ default: m.FleetSignPage })),
);

// Page loading spinner
function PageLoading() {
  return (
    <div className="flex items-center justify-center min-h-64">
      <div className="flex flex-col items-center gap-3">
        <svg className="animate-spin w-8 h-8 text-primary-600" viewBox="0 0 24 24">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <p className="text-sm text-gray-400">Laden...</p>
      </div>
    </div>
  );
}

// Not Found Page
function NotFoundPage() {
  return (
    <div className="flex items-center justify-center min-h-96">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-200">404</h1>
        <p className="text-xl text-gray-600 mt-4">Seite nicht gefunden</p>
        <a
          href="/"
          className="mt-6 inline-block px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Zum Dashboard
        </a>
      </div>
    </div>
  );
}

function App() {
  const { refreshUser } = useAuthStore();

  // Refresh user on app load
  useEffect(() => {
    if (localStorage.getItem('accessToken')) {
      refreshUser();
    }
  }, [refreshUser]);

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <Suspense fallback={<PageLoading />}>
            <LoginPage />
          </Suspense>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <Suspense fallback={<PageLoading />}>
            <ForgotPasswordPage />
          </Suspense>
        }
      />
      <Route
        path="/reset-password"
        element={
          <Suspense fallback={<PageLoading />}>
            <ResetPasswordPage />
          </Suspense>
        }
      />
      <Route
        path="/fleet-sign/:token"
        element={
          <Suspense fallback={<PageLoading />}>
            <FleetSignPage />
          </Suspense>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard */}
        <Route
          index
          element={
            <Suspense fallback={<PageLoading />}>
              <DashboardPage />
            </Suspense>
          }
        />

        {/* Vehicles */}
        <Route
          path="vehicles"
          element={
            <Suspense fallback={<PageLoading />}>
              <VehicleListPage />
            </Suspense>
          }
        />
        <Route
          path="vehicles/:id"
          element={
            <Suspense fallback={<PageLoading />}>
              <VehicleDetailPage />
            </Suspense>
          }
        />

        {/* Processes (Übergaben) */}
        <Route
          path="processes"
          element={
            <Suspense fallback={<PageLoading />}>
              <ProcessListPage />
            </Suspense>
          }
        />
        <Route
          path="processes/:id"
          element={
            <Suspense fallback={<PageLoading />}>
              <ProcessDetailPage />
            </Suspense>
          }
        />

        {/* Workshop */}
        <Route
          path="workshop"
          element={
            <ProtectedRoute
              roles={[
                'FLEET_MANAGER',
                'FLEET_LOCATION_ADMIN',
                'FLEET_COORDINATOR',
                'FLEET_WORKSHOP',
                'ADMIN',
                'SUPER_ADMIN',
              ]}
            >
              <Suspense fallback={<PageLoading />}>
                <WorkshopDashboardPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Service Planning (Wartungsplanung) */}
        <Route
          path="service-planning"
          element={
            <ProtectedRoute
              roles={[
                'FLEET_MANAGER',
                'FLEET_LOCATION_ADMIN',
                'FLEET_COORDINATOR',
                'FLEET_REPORTING',
                'ADMIN',
                'SUPER_ADMIN',
              ]}
            >
              <Suspense fallback={<PageLoading />}>
                <ServicePlanningPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Reports */}
        <Route
          path="reports"
          element={
            <ProtectedRoute
              roles={[
                'FLEET_MANAGER',
                'FLEET_LOCATION_ADMIN',
                'FLEET_COORDINATOR',
                'FLEET_REPORTING',
                'ADMIN',
                'SUPER_ADMIN',
              ]}
            >
              <Suspense fallback={<PageLoading />}>
                <ReportsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Users / Mitarbeiter */}
        <Route
          path="users"
          element={
            <ProtectedRoute
              roles={[
                'FLEET_MANAGER',
                'FLEET_LOCATION_ADMIN',
                'FLEET_COORDINATOR',
                'ADMIN',
                'SUPER_ADMIN',
              ]}
            >
              <Suspense fallback={<PageLoading />}>
                <UserManagementPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Settings */}
        <Route
          path="settings"
          element={
            <ProtectedRoute
              roles={['FLEET_MANAGER', 'FLEET_LOCATION_ADMIN', 'ADMIN', 'SUPER_ADMIN']}
            >
              <Suspense fallback={<PageLoading />}>
                <SettingsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

export default App;
